// src/products/analysis/pages/CryptoCorrelationPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle, BarChart3, Info, UploadCloud, FileText } from 'lucide-react';
import { getTopCryptocurrencies, getCryptoCandles } from '../services/cryptoService';
import { calculateTimeSeriesCorrelation, interpretCorrelation, calculateCorrelation } from '../utils/correlationUtils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import * as XLSX from 'xlsx';

const CryptoCorrelationPage = () => {
  const [cryptos, setCryptos] = useState([]);
  const [crypto1, setCrypto1] = useState(null);
  const [crypto2, setCrypto2] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('30'); // days
  const [uploadError, setUploadError] = useState(null);
  const [uploadedRows, setUploadedRows] = useState([]);
  const [uploadColumns, setUploadColumns] = useState([]);
  const [dateColumn, setDateColumn] = useState('');
  const [customColumn1, setCustomColumn1] = useState('');
  const [customColumn2, setCustomColumn2] = useState('');
  const [customResult, setCustomResult] = useState(null);
  const [customProcessing, setCustomProcessing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const timePeriods = [
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '180', label: '180 Days' },
    { value: '365', label: '1 Year' }
  ];

  const normalizeTimestamp = (rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    if (rawValue instanceof Date) {
      const timestamp = rawValue.getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
    }

    if (typeof rawValue === 'number') {
      if (rawValue > 100000000000) {
        return rawValue;
      }
      if (XLSX?.SSF?.parse_date_code) {
        const parsed = XLSX.SSF.parse_date_code(rawValue);
        if (parsed) {
          const excelDate = new Date(
            parsed.y,
            (parsed.m || 1) - 1,
            parsed.d || 1,
            parsed.H || 0,
            parsed.M || 0,
            parsed.S || 0
          );
          const timestamp = excelDate.getTime();
          if (Number.isFinite(timestamp)) {
            return timestamp;
          }
        }
      }
      return null;
    }

    const parsedDate = new Date(rawValue);
    const timestamp = parsedDate.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const cleaned = value.replace(/,/g, '').trim();
      if (cleaned === '') {
        return null;
      }
      const numeric = Number(cleaned);
      return Number.isFinite(numeric) ? numeric : null;
    }

    return null;
  };

  useEffect(() => {
    loadCryptos();
  }, []);

  useEffect(() => {
    if (crypto1 && crypto2) {
      loadCorrelationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crypto1, crypto2, timePeriod]);

  const resetUploadState = () => {
    setUploadedRows([]);
    setUploadColumns([]);
    setDateColumn('');
    setCustomColumn1('');
    setCustomColumn2('');
    setCustomResult(null);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    resetUploadState();
    setUploadError(null);
    setCustomProcessing(true);
    setUploadedFileName(file.name);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const buffer = new Uint8Array(e.target.result);
        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          throw new Error('The uploaded file does not contain any worksheets.');
        }

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: null });

        if (!rows || rows.length === 0) {
          throw new Error('The uploaded file does not contain any rows.');
        }

        const headers = Object.keys(rows[0] || {}).filter(Boolean);

        if (!headers.length) {
          throw new Error('Unable to detect column headers. Ensure the first row contains column names.');
        }

        const detectedDateColumn =
          headers.find((header) => header.toLowerCase().includes('date')) ||
          headers.find((header) => header.toLowerCase().includes('time')) ||
          null;

        if (!detectedDateColumn) {
          throw new Error('Missing date column. Include a column named "Date" or "Timestamp" in the first row.');
        }

        const processedRows = rows
          .map((row) => {
            const timestamp = normalizeTimestamp(row[detectedDateColumn]);
            return {
              ...row,
              __timestamp: timestamp
            };
          })
          .filter((row) => Number.isFinite(row.__timestamp))
          .sort((a, b) => a.__timestamp - b.__timestamp);

        if (processedRows.length < 2) {
          throw new Error('Not enough valid rows. Ensure the file provides at least two dated entries.');
        }

        const candidateColumns = headers.filter((header) => header !== detectedDateColumn);
        const numericColumns = candidateColumns.filter((column) =>
          processedRows.some((row) => parseNumericValue(row[column]) !== null)
        );

        if (numericColumns.length < 2) {
          throw new Error('Need at least two numeric columns in addition to the date column.');
        }

        setUploadedRows(processedRows);
        setUploadColumns(numericColumns);
        setDateColumn(detectedDateColumn);
        setCustomColumn1(numericColumns[0]);
        setCustomColumn2(numericColumns[1]);
        setCustomProcessing(false);
      } catch (err) {
        console.error('Custom upload parse error:', err);
        setUploadError(err.message || 'Failed to process the uploaded file.');
        setUploadedFileName('');
        setCustomProcessing(false);
      }
    };

    reader.onerror = () => {
      setUploadError('Unable to read the uploaded file. Please try again.');
      setUploadedFileName('');
      setCustomProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleRunCustomCorrelation = () => {
    if (!uploadedRows.length) {
      setUploadError('Upload a file before running correlation.');
      return;
    }

    if (!customColumn1 || !customColumn2 || customColumn1 === customColumn2) {
      setUploadError('Select two different numeric columns for analysis.');
      return;
    }

    setUploadError(null);
    setCustomProcessing(true);

    try {
      const alignedData = uploadedRows
        .map((row) => {
          const value1 = parseNumericValue(row[customColumn1]);
          const value2 = parseNumericValue(row[customColumn2]);
          if (value1 === null || value2 === null) {
            return null;
          }
          return {
            timestamp: row.__timestamp,
            value1,
            value2
          };
        })
        .filter(Boolean);

      if (alignedData.length < 2) {
        throw new Error('Not enough numeric rows to calculate correlation. Double-check the column contents.');
      }

      const x = alignedData.map((row) => row.value1);
      const y = alignedData.map((row) => row.value2);
      const value = calculateCorrelation(x, y);

      setCustomResult({
        correlation: value,
        alignedData,
        dataPoints: alignedData.length,
        column1: customColumn1,
        column2: customColumn2
      });
    } catch (err) {
      setUploadError(err.message || 'Failed to calculate correlation from uploaded data.');
      setCustomResult(null);
    } finally {
      setCustomProcessing(false);
    }
  };

  const loadCryptos = async () => {
    setLoading(true);
    setError(null);
    try {
      const cryptoList = await getTopCryptocurrencies(25); // Align with top 25 listing for selection
      setCryptos(cryptoList);
    } catch (err) {
      setError(`Failed to load cryptocurrencies: ${err.message}`);
      console.error('Crypto loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCorrelationData = async () => {
    if (!crypto1 || !crypto2 || crypto1.id === crypto2.id) {
      setCorrelation(null);
      return;
    }

    setLoadingData(true);
    setError(null);

    try {
      // Fetch historical data for both cryptos in parallel
      const [candles1, candles2] = await Promise.all([
        getCryptoCandles(crypto1.id, parseInt(timePeriod)),
        getCryptoCandles(crypto2.id, parseInt(timePeriod))
      ]);

      // Calculate correlation
      const result = calculateTimeSeriesCorrelation(candles1, candles2);
      setCorrelation(result);
    } catch (err) {
      setError(`Failed to load correlation data: ${err.message}`);
      console.error('Correlation loading error:', err);
      setCorrelation(null);
    } finally {
      setLoadingData(false);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Prepare chart data (aligned by timestamp)
  const chartData = correlation?.alignedData?.map(d => ({
    date: formatDate(d.timestamp),
    timestamp: d.timestamp,
    [crypto1?.symbol || 'Crypto1']: d.value1,
    [crypto2?.symbol || 'Crypto2']: d.value2
  })) || [];

  const interpretation = correlation?.correlation !== null && correlation?.correlation !== undefined
    ? interpretCorrelation(correlation.correlation)
    : null;

  const customChartData = useMemo(() => {
    if (!customResult?.alignedData?.length) {
      return [];
    }
    return customResult.alignedData.map((entry) => ({
      date: formatDate(entry.timestamp),
      timestamp: entry.timestamp,
      [customResult.column1]: entry.value1,
      [customResult.column2]: entry.value2
    }));
  }, [customResult]);

  const customInterpretation = useMemo(() => {
    if (customResult?.correlation === null || customResult?.correlation === undefined) {
      return null;
    }
    return interpretCorrelation(customResult.correlation);
  }, [customResult]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Crypto Correlation Analysis</h1>
        <p className="mt-1 text-sm text-gray-500">
          Calculate correlation between two cryptocurrencies over time
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading cryptocurrencies...</span>
        </div>
      )}

      {/* Selection Form */}
      {!loading && cryptos.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Crypto 1 Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Cryptocurrency
              </label>
              <select
                value={crypto1?.id || ''}
                onChange={(e) => {
                  const selected = cryptos.find(c => c.id === e.target.value);
                  setCrypto1(selected || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select crypto...</option>
                {cryptos.map(crypto => (
                  <option key={crypto.id} value={crypto.id}>
                    {crypto.name} ({crypto.symbol})
                  </option>
                ))}
              </select>
            </div>

            {/* Crypto 2 Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Second Cryptocurrency
              </label>
              <select
                value={crypto2?.id || ''}
                onChange={(e) => {
                  const selected = cryptos.find(c => c.id === e.target.value);
                  setCrypto2(selected || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select crypto...</option>
                {cryptos.filter(c => c.id !== crypto1?.id).map(crypto => (
                  <option key={crypto.id} value={crypto.id}>
                    {crypto.name} ({crypto.symbol})
                  </option>
                ))}
              </select>
            </div>

            {/* Time Period Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Period
              </label>
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {timePeriods.map(period => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Custom Upload Instructions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <UploadCloud className="w-5 h-5 text-blue-600" />
              Upload Your Own Time-Series Data
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Bring in CSV or Excel files to compare two custom data series. Files must include a header row with a
              date column and at least two numeric columns. Supported formats: <code>.csv</code>, <code>.xlsx</code>, <code>.xls</code>.
            </p>
            <div className="mt-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Required structure</p>
              <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>Column headers in the first row.</li>
                <li>Date column named e.g. <code>Date</code> or <code>Timestamp</code> using ISO (<code>2024-01-15</code>) or Excel date formats.</li>
                <li>At least two numeric columns representing the series you want to compare.</li>
              </ul>
              <p className="mt-3 text-xs font-semibold text-gray-700 uppercase tracking-wide">Example</p>
              <pre className="mt-1 bg-white border border-gray-200 rounded-md text-xs text-gray-700 p-2 overflow-x-auto">
{`Date,BTC_USD,ETH_USD,ALT_INDEX
2024-01-02,42750.34,2230.18,135.2
2024-01-03,43120.11,2289.42,138.5`}
              </pre>
            </div>
          </div>
          <div className="w-full max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              Select CSV or Excel file
            </label>
            <input
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-l-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {uploadedFileName && (
              <p className="mt-2 text-xs text-gray-500 truncate">Loaded: {uploadedFileName}</p>
            )}
            {uploadError && (
              <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {uploadError}
              </p>
            )}
          </div>
        </div>

        {uploadColumns.length >= 2 && (
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Date Column
                </label>
                <input
                  type="text"
                  value={dateColumn}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Series A
                </label>
                <select
                  value={customColumn1}
                  onChange={(e) => setCustomColumn1(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {uploadColumns.map((column) => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Series B
                </label>
                <select
                  value={customColumn2}
                  onChange={(e) => setCustomColumn2(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {uploadColumns.map((column) => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleRunCustomCorrelation}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={customProcessing}
                >
                  {customProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run Correlation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Correlation Results */}
      {loadingData && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Calculating correlation...</span>
        </div>
      )}

      {!loadingData && correlation && correlation.correlation !== null && (
        <div className="space-y-6">
          {/* Correlation Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Correlation Result</h2>
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Correlation Coefficient */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Correlation Coefficient</p>
                <p className="text-3xl font-bold text-gray-900">
                  {correlation.correlation !== null
                    ? correlation.correlation.toFixed(4)
                    : 'N/A'}
                </p>
                <p className="text-xs text-gray-400 mt-1">Range: -1 to +1</p>
              </div>

              {/* Strength */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Strength</p>
                <p className="text-2xl font-bold text-gray-900">{interpretation?.strength || 'N/A'}</p>
                <p className="text-xs text-gray-400 mt-1">{interpretation?.direction || 'N/A'} Correlation</p>
              </div>

              {/* Data Points */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Data Points</p>
                <p className="text-2xl font-bold text-gray-900">{correlation.dataPoints || 0}</p>
                <p className="text-xs text-gray-400 mt-1">Aligned timestamps</p>
              </div>
            </div>

            {/* Interpretation */}
            {interpretation?.description && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Interpretation</p>
                    <p className="text-sm text-blue-800 mt-1">{interpretation.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Price Comparison: {crypto1?.symbol} vs {crypto2?.symbol}
              </h3>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis 
                      dataKey="date" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80} 
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      tickFormatter={(value) => `$${value.toFixed(0)}`} 
                      domain={['dataMin', 'dataMax']} 
                      width={80}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`$${parseFloat(value).toFixed(2)}`, name]} 
                      labelFormatter={(label) => `Date: ${label}`}
                      contentStyle={{ 
                        backgroundColor: '#FFFFFF', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '6px' 
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey={crypto1?.symbol || 'Crypto1'} 
                      stroke="#2563eb" 
                      strokeWidth={2} 
                      dot={false} 
                      activeDot={{ r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={crypto2?.symbol || 'Crypto2'} 
                      stroke="#dc2626" 
                      strokeWidth={2} 
                      dot={false} 
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Message */}
      {!loadingData && !correlation && crypto1 && crypto2 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-2 text-yellow-800">
          <Info className="w-5 h-5 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Select two different cryptocurrencies to calculate correlation</p>
            <p className="text-xs mt-1">
              Correlation measures how closely two assets move together. 
              +1 means perfect positive correlation, -1 means perfect negative correlation, 0 means no correlation.
            </p>
          </div>
        </div>
      )}

      {!customProcessing && customResult && customResult.correlation !== null && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Uploaded Data Correlation</h2>
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Columns Compared</p>
                <p className="text-sm text-gray-900 font-semibold">
                  {customResult.column1} vs {customResult.column2}
                </p>
                <p className="text-xs text-gray-400 mt-1">Rows: {customResult.dataPoints}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Correlation Coefficient</p>
                <p className="text-3xl font-bold text-gray-900">
                  {customResult.correlation !== null ? customResult.correlation.toFixed(4) : 'N/A'}
                </p>
                <p className="text-xs text-gray-400 mt-1">Range: -1 to +1</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Interpretation</p>
                <p className="text-lg font-bold text-gray-900">{customInterpretation?.strength || 'Unknown'}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {customInterpretation?.direction || 'Unknown'} Correlation
                </p>
              </div>
            </div>

            {customInterpretation?.description && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Interpretation</p>
                    <p className="text-sm text-blue-800 mt-1">{customInterpretation.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {customChartData.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Uploaded Series: {customResult.column1} vs {customResult.column2}
              </h3>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={customChartData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(value) => value.toLocaleString()}
                      domain={['dataMin', 'dataMax']}
                      width={80}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        [Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 }), name]
                      }
                      labelFormatter={(label) => `Date: ${label}`}
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey={customResult.column1}
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey={customResult.column2}
                      stroke="#dc2626"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CryptoCorrelationPage;

